import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { AlertService } from '../../../../../services/alert.service';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TemplatesService } from '../../../../../services/templates.service';
import { PROFILES } from '../../../../../constants';
import { useSession } from '../../../../../hooks/use-session.hook';
import Select from 'react-select';
import InvalidFeedback from '../../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../../components/forms/icon.component';
import Title from '../../../../../components/forms/titles.component';

import EventsSection, { getChannelEventsConfig } from './events.component';
import { useLocation } from 'react-router-dom';

function channelWa({ onValidationChange }: { onValidationChange?: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const location = useLocation();
  const showEvents = location.pathname.includes('sendCampaigns/flows-campaigns');
  const [accountWahtsapp, setAccountWahtsapp] = useState<{ value: string; label: string }[]>([]);
  const [templatesWA, setTemplatesWA] = useState<any[]>([]);
  const [previewWA, setPreviewWA] = useState<any>();
  const [variablesBySection, setVariablesBySection] = useState<{ [key: string]: any[] }>({});
  const [emptyAccountWA, setEmptyAccountWA] = useState(false);
  const [emptyTemplatesWA, setEmptyTemplatesWA] = useState(false);
  const [eventsValid, setEventsValid] = useState(true);

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const { control, watch, setValue, register } = useFormContext();

  const formValues = useMemo(() => {
    const values = watch();
    return {
      headersSelects: values.headersSelects,
      account: values.account,
      channel: values.channel,
      accountsWa: values.accountsWa,
      typeCampaign: values.typeCampaign,
      templatesWa: values.templatesWa,
      channelfallback: values.channelfallback,
      accountCahnnels: values.accountCahnnels,
      channelFlows: values.channelFlows,
      events: values.events
    };
  }, [watch()]);

  const accountWAMessage = useMemo(() => {
    if (!emptyAccountWA) return '';
    return session.profile === PROFILES.ADMIN
      ? 'La cuenta con la que estás trabajando no tiene una cuenta de WhatsApp registrada'
      : 'La cuenta seleccionada en el paso 1 no tiene una cuenta de WhatsApp registrada.';
  }, [emptyAccountWA, session.profile]);

  const templatesWAMessage = useMemo(() => {
    if (!emptyTemplatesWA) return '';
    return session.profile === PROFILES.ADMIN
      ? 'La cuenta con la que estás trabajando no tiene no tiene plantillas registradas.'
      : 'La cuenta de WhatsApp seleccionada no tiene plantillas registradas.';
  }, [emptyTemplatesWA, session.profile]);

  const sectionKeyLabel: Record<string, string> = {
    HEADER: 'Encabezado',
    BODY: 'Cuerpo',
    FOOTER: 'Píe',
    BUTTONS: 'Botones de acción (URL)'
  };

  const [vars, setVars] = useState<Record<string, Record<string, string>>>({
    header: {},
    body: {},
    footer: {},
    buttons: {}
  });

  const fallbackChannelOptions = useMemo(() => {
    if (!formValues.channelFlows || !Array.isArray(formValues.channelFlows)) return [];

    const channelLabels: Record<string, string> = {
      sms: 'SMS',
      rcs: 'RCS',
      mail: 'Email'
    };

    return formValues.channelFlows
      .filter((channel) => channel !== 'wa')
      .map((channel) => ({
        value: channel,
        label: channelLabels[channel] || channel.toUpperCase()
      }));
  }, [formValues.channelFlows]);

  const followUpTemplateOptions = useMemo(() => {
    if (!templatesWA || !Array.isArray(templatesWA)) return [];
    return templatesWA.filter((template) => template.value !== formValues.templatesWa);
  }, [templatesWA, formValues.templatesWa]);

  const eventsConfig = useMemo(() => {
    return getChannelEventsConfig('wa', fallbackChannelOptions, followUpTemplateOptions);
  }, [fallbackChannelOptions, followUpTemplateOptions]);

  const getTemplatesWA = useCallback(async () => {
    if (!formValues.accountsWa) return;

    try {
      const response = await TemplatesService.getTemplatesAccountWa(formValues.accountsWa);
      setEmptyTemplatesWA((!response || response.length === 0) && !!formValues.accountsWa);
      const allTemplates = Object.values(response).flat() as any[];
      const filtered = allTemplates.filter((template) => {
        const status = template.status?.toUpperCase();
        return status === 'APPROVED';
      });

      const finalTemplates = filtered.map(({ _id, name, ...rest }) => ({
        value: _id,
        label: name,
        fullTemplate: { _id, name, ...rest }
      }));

      setTemplatesWA(finalTemplates);
    } catch (errorCode: any) {
      setValue('templatesWa', '');
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  }, [formValues.accountsWa, setValue]);

  const resetConfigVars = useCallback(() => {
    setVars({
      HEADER: {},
      BODY: {},
      FOOTER: {},
      BUTTONS: {}
    });
    setVariablesBySection({});
  }, []);

  const getVariablesByType = useCallback((components: any[]) => {
    const variableRegex = /{{([^{}]*)}}/g;
    const result: Record<string, string[]> = {};

    components.forEach((component) => {
      const { type, text, buttons } = component;
      let matches: string[] = [];
      if (!result[type]) {
        result[type] = [];
      }
      if (text) {
        matches = [...text.matchAll(variableRegex)].map((m) => `{{${m[1]}}}`);
        result[type].push(...matches);
      }
      if (type === 'BUTTONS' && buttons?.length) {
        buttons.forEach((btn: any) => {
          if (btn.text) {
            const btnMatches = [...btn.text.matchAll(variableRegex)].map((m) => `{{${m[1]}}}`);
            result[type].push(...btnMatches);
          }
          if (btn.type === 'URL' && Array.isArray(btn.example)) {
            btn.example.forEach((url: string) => {
              const parts = url.split('/');
              const lastPart = parts[parts.length - 1];
              if (lastPart) {
                result[type].push(`{{1}}`);
              }
            });
          }
        });
      }
    });

    return result;
  }, []);

  const renderVariableSelects = useCallback(
    (sectionName: string, variables: string[], sectionKey: string) => {
      const urlButtonsWithExample =
        previewWA?.components
          ?.find((c: any) => c.type === 'BUTTONS')
          ?.buttons?.filter(
            (btn: any) => btn.type === 'URL' && Array.isArray(btn.example) && btn.example.length > 0
          ) || [];

      return (
        <div key={`variables-${sectionKey}`}>
          <div className="fs-3 fw-bolder text-info">{sectionName}</div>
          <div className="fs-4 mb-7">
            {sectionName.toLowerCase() === 'botones de acción (url)'
              ? 'Tus botones de tipo URL dinámica tienen una variable en la URL. Selecciona la columna para esa variable.'
              : `Selecciona una columna para cada una de las variables en ${sectionName.toLowerCase()} de tu plantilla.`}
          </div>
          <div className="fs-6 d-flex justify-content-between mb-4">
            <div className="fw-semibold fw-bolder fs-3">Variable</div>
            <div className="d-flex fw-bold fs-3 required">Columna</div>
          </div>
          {variables.map((variable, idx) => {
            const button = sectionKey === 'BUTTONS' ? urlButtonsWithExample[idx] : null;
            const buttonText = button?.text || '';
            const exampleUrls: string[] = button?.example || [];

            return (
              <React.Fragment key={`${sectionKey}-${idx}`}>
                <div className="fs-6 d-flex justify-content-between align-items-start my-4 gap-4">
                  <div className="fw-semibold fs-3">
                    {variable}
                    <div className="mt-2 fs-6">
                      {exampleUrls.length > 0 &&
                        exampleUrls.map((url, i) => (
                          <React.Fragment key={`example-url-${idx}-${i}`}>
                            <div>
                              <strong className="text-dark fw-bolder">Texto del botón:</strong>{' '}
                              <strong className="text-info">{buttonText}</strong>
                            </div>
                            <div>
                              <strong className="text-dark fw-bolder">URL del botón:</strong>{' '}
                              <strong className="text-info">
                                {url.replace(/\/[^/]*$/, `/{{${i + 1}}}`)}
                              </strong>
                            </div>
                          </React.Fragment>
                        ))}
                    </div>
                  </div>

                  <div className="fw-bold" style={{ minWidth: '250px' }}>
                    <div className="fv-row">
                      <Controller
                        control={control}
                        name={`columnsVariables.${sectionKey}[${idx}]`}
                        rules={{ required: true }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <>
                            <Select
                              options={formValues.headersSelects}
                              className={
                                error
                                  ? 'form-select-custom is-invalid w-250px'
                                  : 'form-select-custom w-250px'
                              }
                              classNamePrefix="form-select-custom"
                              placeholder={'Selecciona una columna'}
                              onChange={(selectedOption) => {
                                const selectedValue = selectedOption ? selectedOption.value : null;
                                onChange(selectedValue);

                                if (selectedValue !== null) {
                                  setVars((prev) => {
                                    const updated = { ...prev };

                                    if (sectionKey === 'BUTTONS') {
                                      const componentButtons = previewWA?.components?.find(
                                        (c: any) => c.type === 'BUTTONS'
                                      );
                                      const buttons = componentButtons?.buttons || [];

                                      let currentUrlIndex = null;
                                      let urlCounter = -1;

                                      for (let i = 0; i < buttons.length; i++) {
                                        if (
                                          buttons[i].type === 'URL' &&
                                          Array.isArray(buttons[i].example)
                                        ) {
                                          urlCounter++;
                                          if (urlCounter === idx) {
                                            currentUrlIndex = i;
                                            break;
                                          }
                                        }
                                      }

                                      if (currentUrlIndex !== null) {
                                        updated[sectionKey] = {
                                          ...updated[sectionKey],
                                          [currentUrlIndex]: selectedValue
                                        };
                                      }
                                    } else {
                                      updated[sectionKey] = {
                                        ...updated[sectionKey],
                                        [variable]: selectedValue
                                      };
                                    }

                                    return updated;
                                  });
                                }
                              }}
                              value={
                                formValues.headersSelects?.find(
                                  (option: any) => option.value === value
                                ) || null
                              }
                              noOptionsMessage={({ inputValue }) =>
                                inputValue
                                  ? t('general.no-options', { inputValue })
                                  : t('general.no-options')
                              }
                            />
                            <InvalidFeedback error={error} />
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      );
    },
    [previewWA, formValues.headersSelects, control, t]
  );

  useEffect(() => {
    if (formValues.accountCahnnels?.whatsapp) {
      const filtered = formValues.accountCahnnels.whatsapp.filter(
        (item: any) => item.status === true && item.config?.status === true
      );

      if (filtered.length === 0) {
        setAccountWahtsapp([]);
        setEmptyAccountWA(true);
        return;
      }

      const options = filtered.map(({ config }: any) => ({
        value: config._id,
        label: config.name
      }));
      setEmptyAccountWA(false);
      setAccountWahtsapp(options);
    } else {
      setAccountWahtsapp([]);
      setEmptyAccountWA(true);
    }
  }, [
    formValues.account,
    formValues.channel,
    formValues.channelfallback,
    formValues.accountCahnnels
  ]);

  useEffect(() => {
    setValue('vars', vars);
  }, [vars, setValue]);

  useEffect(() => {
    const baseValidation = !!formValues.accountsWa && !!formValues.templatesWa;
    const isValid = baseValidation && (showEvents ? eventsValid : true);

    if (onValidationChangeRef.current) {
      onValidationChangeRef.current(isValid);
    }
  }, [formValues.accountsWa, formValues.templatesWa, eventsValid]);

  useEffect(() => {
    if (formValues.channel !== 'wa') {
      setValue('templatesWa', '');
      setValue('accountsWa', '');
      setTemplatesWA([]);
      setVars({ HEADER: {}, BODY: {}, FOOTER: {}, BUTTONS: {} });
      setVariablesBySection({});
    }
  }, [formValues.channel, formValues.channelfallback, setValue]);

  useEffect(() => {
    const shouldClearFields =
      formValues.typeCampaign !== 'FALLBACK' ||
      (formValues.typeCampaign === 'FALLBACK' && formValues.channel !== 'wa');

    if (shouldClearFields) {
      setValue('templatesWa', '');
      setValue('accountsWa', '');
      setTemplatesWA([]);
      setVars({ HEADER: {}, BODY: {}, FOOTER: {}, BUTTONS: {} });
      setVariablesBySection({});
    }
  }, [formValues.channel, formValues.channelfallback, formValues.typeCampaign, setValue]);

  useEffect(() => {
    if (formValues.account) {
      setValue('templatesWa', '');
      setValue('accountsWa', '');
      setTemplatesWA([]);
      setEmptyTemplatesWA(false);
    }
    resetConfigVars();
  }, [formValues.account, setValue, resetConfigVars]);

  useEffect(() => {
    setValue('previewTemplateWa', previewWA);
  }, [previewWA, setValue]);

  useEffect(() => {
    if (formValues.accountsWa) {
      setValue('templatesWa', '');
      setTemplatesWA([]);
      getTemplatesWA();
    }
  }, [formValues.accountsWa, setValue, getTemplatesWA]);

  useEffect(() => {
    if (formValues.templatesWa) {
      resetConfigVars();
      const selectedTemplate = templatesWA.find(
        (template) => template.value === formValues.templatesWa
      );
      if (selectedTemplate?.fullTemplate) {
        setPreviewWA(selectedTemplate.fullTemplate);
      } else {
        setPreviewWA([]);
      }
    } else {
      setPreviewWA([]);
    }
  }, [formValues.templatesWa, templatesWA, resetConfigVars]);

  useEffect(() => {
    if (formValues.templatesWa && previewWA?.components) {
      resetConfigVars();
      const variables = getVariablesByType(previewWA.components);
      setVariablesBySection(variables);
      Object.entries(variables).forEach(([sectionKey, varsArray]) => {
        varsArray.forEach((_, idx: number) => {
          setValue(`columnsVariables.${sectionKey}[${idx}]`, null);
        });
      });
    }
  }, [formValues.templatesWa, previewWA, resetConfigVars, getVariablesByType, setValue]);

  return (
    <>
      <div className="separator separator-dashed separator-content text-dark mb-15">
        <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="w-30px logo-minimize" />
      </div>
      <div className="mb-5 mb-xxl-8">
        <div className="text-left mb-3">
          <Title icon="ki-whatsapp" title={`Configuración WhatsApp`} subtitle={''} type="primary" />
        </div>
        <div className="pt-1">
          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Cuentas</span>
                <span className="fs-6">Elige la cuenta por la que saldrán los mensaje.</span>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-12 z-index-3">
              <div className="fv-row  mb-5 z-index-3">
                <Controller
                  control={control}
                  name="accountsWa"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={accountWahtsapp}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? t('general.no-options', { inputValue })
                            : t('general.no-options')
                        }
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={'Selecciona una cuenta de WhatsApp'}
                        onChange={(
                          selectedOption: {
                            value: string;
                            label: string;
                          } | null
                        ) => {
                          onChange(selectedOption ? selectedOption.value : null);
                        }}
                        value={
                          accountWahtsapp.find(
                            (option: { value: string; label: string }) => option.value === value
                          ) || null
                        }
                      />
                      <InvalidFeedback error={error} />
                    </>
                  )}
                />
              </div>
            </div>
            {emptyAccountWA && (
              <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
                <Icon icon="ki-information-2" size="fs-2hx" type="info" />
                <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                  <h5 className="mb-1">¡ Atención !</h5>
                  <span>{accountWAMessage}</span>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Plantillas</span>
                <span className="fs-6">Elige la plantilla del mensaje.</span>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-12 z-index-3">
              <div className="fv-row  mb-5 z-index-3">
                <Controller
                  control={control}
                  name="templatesWa"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={templatesWA}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? t('general.no-options', { inputValue })
                            : t('general.no-options')
                        }
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={'Selecciona tu plantilla'}
                        onChange={(
                          selectedOption: {
                            value: string;
                            label: string;
                          } | null
                        ) => {
                          onChange(selectedOption ? selectedOption.value : null);
                        }}
                        value={
                          templatesWA.find(
                            (option: { value: string; label: string }) => option.value === value
                          ) || null
                        }
                      />
                      <InvalidFeedback error={error} />
                    </>
                  )}
                />
              </div>
            </div>
          </div>
          {emptyTemplatesWA && (
            <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
              <Icon icon="ki-information-2" size="fs-2hx" type="info" />
              <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                <h5 className="mb-1">¡ Atención !</h5>
                <span>{templatesWAMessage}</span>
              </div>
            </div>
          )}
        </div>

        {/* Sección de variables */}
        {formValues.templatesWa &&
          variablesBySection &&
          Object.values(variablesBySection).some((vars) => vars.length > 0) && (
            <>
              <div className="separator separator-dashed separator-content text-dark border-2 mb-15">
                <img
                  alt="Logo"
                  src="/assets/media/logos/logo-c.svg"
                  className="w-30px logo-minimize"
                />
              </div>
              <div className="text-left mb-3">
                <Title
                  icon="ki-gear"
                  size="fs-2hx"
                  title="Configuración de variables"
                  type="info"
                />
              </div>

              {Object.entries(variablesBySection).map(([sectionKey, vars], idx, arr) =>
                vars.length > 0 ? (
                  <React.Fragment key={sectionKey}>
                    {renderVariableSelects(sectionKeyLabel[sectionKey], vars, sectionKey)}
                    {idx < arr.length - 1 && (
                      <div className="separator separator-dashed border-2 my-10"></div>
                    )}
                  </React.Fragment>
                ) : null
              )}
            </>
          )}

        {showEvents ? (
          <EventsSection
            channel="wa"
            events={eventsConfig}
            fallbackChannelOptions={fallbackChannelOptions}
            templateOptions={followUpTemplateOptions}
            onValidationChange={setEventsValid}
          />
        ) : null}
      </div>
    </>
  );
}

export default channelWa;
